/**
 * Admin Performance Pool Payout Manager
 * v4.3.85 — Resume Payout button now actually runs. The handleFinalize
 *   guard `if (status === 'Finalized') return;` short-circuited every
 *   click on a partially-finalized date, even though the v4.3.82 button-
 *   enabled logic explicitly allowed resuming when unawarded > 0. The
 *   guard now matches the render-side `fullyDone` condition (Finalized
 *   AND every projected winner already recorded) so a partial run can
 *   be resumed. The award-pool-winner endpoint is already idempotent
 *   (409 ALREADY_AWARDED on retry) so re-running over already-awarded
 *   teams remains safe — they are silently counted as already-paid.
 *   The confirmation prompt now reflects Resume vs Finalize mode.
 *
 * v4.3.84 — Date navigation (prev/next + min/max), status reconciliation
 *   view showing Unclaimed/Paid/Blocked per record, and Force Payout button
 *   for unclaimed records + Unblock & Pay for overpayment records. New
 *   endpoint /api/admin/force-pool-payout handles individual record payouts
 *   with atomic lock, USDT credit, share purchase, cascade, and full audit.
 *   Preview endpoint now returns existingRecords with enriched team/leader info.
 *
 * v4.3.82 — Per-winner sequential "Award → Email → Next" flow.
 *   Finalize now loops through the preview and calls the new
 *   /api/admin/award-pool-winner endpoint once per winner. That endpoint
 *   credits USDT + shares + cascade AND sends the congratulatory email
 *   inside the SAME server invocation, so the email can never be skipped
 *   by a client-side timeout or navigation. Mirrors the pattern used by
 *   CompetitionWinnersManager ("award prize, send email, move to next").
 *   Per-winner progress is surfaced in the banner during the run.
 *
 * v4.3.81 — Server-side Resend Winner Emails button. When a date is
 *   already Finalized, admins can trigger a re-send of the congratulatory
 *   email via the new /api/admin/resend-pool-winner-emails endpoint, which
 *   runs entirely server-side (no browser loop) and writes every attempt
 *   to email_delivery_log for audit.
 *
 * v4.3.80 — Pro-rata tie-group display (T1 policy). Tied ranks are shown
 *   with a "Tied N-way" badge and the combined prize is split equally
 *   among tied teams (see api/admin/finalize-performance-pool.js). Rank
 *   rows past 10 appear when a tie group at the boundary extends past
 *   rank 10 (all tied teams get paid).
 *
 * v4.3.78 — Refactored to a manual, admin-controlled Direct-Credit workflow.
 *   The daily cron (/api/cron/process-performance-pool) is retired. Admins
 *   pick a date, review the computed Top 50 via the "Preview Winners" action
 *   (calls /api/admin/finalize-performance-pool with commit=false), and then
 *   click "Finalize & Payout" which commits the same endpoint with
 *   commit=true. The backend credits USDT directly, inserts active share
 *   purchases with payment_method='Performance Pool' (excluded from both
 *   pool-escrow and team-volume triggers per the v4.3.78 DB migration),
 *   runs the multi-level commission cascade on the share portion, and
 *   returns a winners-with-email payload so this UI can dispatch
 *   congratulatory emails via /api/send-mass-email (mirror of
 *   CompetitionWinnersManager.tsx).
 *
 *   Previous per-team "Pay" buttons are removed. Date status is surfaced
 *   as a single badge: Finalized / Ready / No Activity.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import { getSessionHeaders } from '../../lib/serverSession';
import { POOL_CONTRIBUTION_PERCENT } from '../../lib/goldDiggersConstants.js';

// v4.3.78: scoring / distribution constants live server-side only.
// The authoritative computation happens in /api/admin/finalize-performance-pool
// using PAYOUT_DISTRIBUTION and FLAT_PAYOUT_SPLIT from lib/goldDiggersConstants.js.
// This component consumes the computed rows via the preview payload.

interface PreviewWinner {
  team_id: number; team_name: string; leader_id: number;
  leader_username: string; leader_full_name?: string; leader_email?: string;
  category: string; total_volume_usd: number; total_shares: number;
  rank: number; pct: number;
  prize_amount?: number; bonus_amount?: number; bonus_pct?: number;
  amount_awarded: number; usdt_portion: number; share_portion: number;
  tie_group_size?: number; tie_rank_start?: number;
}

interface ExistingRecord {
  id: string; team_id: number; rank: number; claimed: boolean;
  claimed_at: string | null; paid_at: string | null;
  is_overpayment: boolean; amount_awarded: number;
  usdt_portion: number; share_portion: number; pool_total: number;
  source: string | null; team_name: string; leader_id: number;
  leader_username: string; leader_full_name: string;
  record_status: 'unclaimed' | 'paid' | 'blocked';
}

type DateStatus = 'Finalized' | 'Ready' | 'No Activity' | 'Unknown';

const POOL_LAUNCH_DATE = '2026-04-14';

export const AdminPerformancePoolPayouts: React.FC = () => {
  const [poolTotal, setPoolTotal] = useState(0);
  const [preview, setPreview] = useState<PreviewWinner[]>([]);
  const [status, setStatus] = useState<DateStatus>('Unknown');
  const [existingCount, setExistingCount] = useState(0);
  const [existingRecords, setExistingRecords] = useState<ExistingRecord[]>([]);
  const [poolHistory, setPoolHistory] = useState<any[]>([]);
  const [teams, setTeams] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [previewing, setPreviewing] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [resending, setResending] = useState(false);
  const [forcingPayout, setForcingPayout] = useState<string | null>(null);
  const [message, setMessage] = useState<{ kind: 'info' | 'success' | 'error'; text: string } | null>(null);
  const [deleteTeamId, setDeleteTeamId] = useState('');
  const [deleteResult, setDeleteResult] = useState('');
  const [activeView, setActiveView] = useState<'payouts' | 'teams'>('payouts');
  const [selectedDate, setSelectedDate] = useState(() => {
    const y = new Date(); y.setUTCDate(y.getUTCDate() - 1);
    return y.toISOString().split('T')[0];
  });

  // Date navigation helpers (v4.3.84)
  const navigateDate = (delta: number) => {
    const d = new Date(selectedDate + 'T12:00:00Z');
    d.setUTCDate(d.getUTCDate() + delta);
    const iso = d.toISOString().split('T')[0];
    if (iso >= POOL_LAUNCH_DATE) {
      setSelectedDate(iso);
      setMessage(null);
    }
  };
  const todayStr = new Date().toISOString().split('T')[0];

  // Pull preview from the authoritative backend endpoint. No computation
  // happens client-side — the UI is a thin display layer over
  // /api/admin/finalize-performance-pool?commit=false.
  const loadPreview = useCallback(async (date: string) => {
    setPreviewing(true);
    setMessage(null);
    try {
      const r = await fetch('/api/admin/finalize-performance-pool', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ date, commit: false }),
      });
      const d = await r.json();
      if (!r.ok || !d.success) {
        setMessage({ kind: 'error', text: d.error || 'Preview failed' });
        setPreview([]); setStatus('Unknown'); setPoolTotal(0); setExistingCount(0); setExistingRecords([]);
      } else {
        setPreview(d.projected || []);
        setStatus((d.status as DateStatus) || 'Unknown');
        setPoolTotal(parseFloat(d.poolTotal) || 0);
        setExistingCount(d.existingWinners || 0);
        setExistingRecords(d.existingRecords || []);
      }
    } catch (e: any) {
      setMessage({ kind: 'error', text: e.message || 'Preview failed' });
    }
    setPreviewing(false);
  }, []);

  const loadAuxData = useCallback(async () => {
    setLoading(true);
    try {
      // v4.8.253: via the admin server endpoint — the RPC is service_role-only, so a
      // direct browser call returned 403 and left this panel without pool history.
      const histResp = await fetch('/api/admin/pool-history', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ limit: 14 })
      });
      const histJson = await histResp.json();
      setPoolHistory(Array.isArray(histJson?.data) ? histJson.data : []);
      const { data: teamData } = await supabase.from('teams')
        .select('id, leader_id, name, category, users!leader_id(username)').eq('is_active', true).limit(200);
      if (teamData) {
        setTeams(teamData.map((t: any) => ({ ...t, leader_username: t.users?.username || '?' })));
      }
    } catch (e) { console.error('Admin pool aux load error:', e); }
    setLoading(false);
  }, []);

  useEffect(() => { loadAuxData(); }, [loadAuxData]);
  useEffect(() => { loadPreview(selectedDate); }, [selectedDate, loadPreview]);

  // v4.3.82: per-winner award+email loop. Each iteration is one HTTP
  // call to /api/admin/award-pool-winner, which does USDT credit + share
  // purchase + cascade + audit + notification + Resend email inside a
  // SINGLE server invocation, then returns. Idempotent via DB unique
  // index — ALREADY_AWARDED on re-run just skips to the next winner.

  // Finalize & Payout — per-winner sequential loop (v4.3.82). Calls
  // /api/admin/award-pool-winner once per team. Each server call does
  // award + email atomically; client only drives the loop and displays
  // progress. Safe to re-run: already-awarded winners return 409 and
  // are counted as "already paid".
  // v4.3.85: the early-return now mirrors the render-side `fullyDone`
  // check so partially-finalized dates can be resumed (the prior guard
  // bailed out the moment status flipped to 'Finalized', which could
  // happen as soon as the first winner was awarded).
  const handleFinalize = async () => {
    if (preview.length === 0) return;
    const unawarded = Math.max(0, preview.length - existingCount);
    if (unawarded === 0) return;
    const isResume = existingCount > 0;
    const total = preview.reduce((s, w) => s + w.amount_awarded, 0);
    const remainingTotal = preview
      .slice(existingCount)
      .reduce((s, w) => s + w.amount_awarded, 0);
    const msg = (isResume
      ? `Resume Payout for ${selectedDate}?\n\n` +
        `• ${existingCount} of ${preview.length} winners already awarded\n` +
        `• ${unawarded} winners remaining (~$${remainingTotal.toFixed(2)})\n` +
        `• Pool total: $${poolTotal.toFixed(2)}\n\n`
      : `Finalize & Payout for ${selectedDate}?\n\n` +
        `• ${preview.length} winners\n` +
        `• Pool total: $${poolTotal.toFixed(2)}\n` +
        `• Rank prizes + equal remainder bonus: $${total.toFixed(2)}\n\n`) +
      `Each winner will be processed ONE AT A TIME:\n` +
      `  1. Credit USDT (80%) + shares (20%) + referral cascade\n` +
      `  2. Send congratulatory email via Resend (logged to email_delivery_log)\n` +
      `  3. Move to the next winner\n\n` +
      `Already-awarded teams are skipped automatically (server returns 409).\n` +
      `This action cannot be undone. Do not navigate away until complete.`;
    if (!confirm(msg)) return;
    setFinalizing(true);

    let awarded = 0;
    let alreadyAwarded = 0;
    let awardFailed = 0;
    let emailSent = 0;
    let emailFailed = 0;
    const rowErrors: string[] = [];

    for (let i = 0; i < preview.length; i++) {
      const w = preview[i];
      setMessage({
        kind: 'info',
        text: `Processing ${i + 1}/${preview.length}: #${w.rank} ${w.team_name} — $${w.amount_awarded.toFixed(2)}…`,
      });
      try {
        const resp = await fetch('/api/admin/award-pool-winner', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
          body: JSON.stringify({ date: selectedDate, team_id: w.team_id }),
        });
        const d = await resp.json();
        if (resp.status === 409 && d.code === 'ALREADY_AWARDED') {
          alreadyAwarded++;
        } else if (!resp.ok || !d.success) {
          awardFailed++;
          rowErrors.push(`#${w.rank} ${w.team_name}: ${d.error || 'unknown'}`);
          continue;
        } else {
          awarded++;
          if (d.email_status === 'sent') emailSent++;
          else emailFailed++;
        }
      } catch (e: any) {
        awardFailed++;
        rowErrors.push(`#${w.rank} ${w.team_name}: ${e.message || 'network error'}`);
      }
    }

    const summary =
      `✅ ${selectedDate}: awarded ${awarded} • already-awarded ${alreadyAwarded} • ` +
      `failed ${awardFailed}. Emails: ${emailSent} sent / ${emailFailed} failed.` +
      (rowErrors.length ? ` Errors: ${rowErrors.slice(0, 3).join(' | ')}${rowErrors.length > 3 ? ' …' : ''}` : '');
    setMessage({
      kind: (awardFailed > 0 || emailFailed > 0) ? 'error' : 'success',
      text: summary,
    });
    await loadPreview(selectedDate);
    setFinalizing(false);
  };

  // v4.3.81: server-side resend for already-finalized dates. Unlike the
  // in-finalize sendWinnerEmails loop (which ran in the browser and left
  // no audit trail), this delegates to /api/admin/resend-pool-winner-emails
  // which reads performance_pool_winners, sends via Resend server-side,
  // and writes every attempt to email_delivery_log.
  const handleResendWinnerEmails = async () => {
    if (resending) return;
    if (status !== 'Finalized') return;
    if (!confirm(`Resend winner emails for ${selectedDate}?\n\nThis will re-send the congratulatory email to every finalized winner. Each send is logged to email_delivery_log for audit. Safe to run multiple times.`)) return;
    setResending(true);
    setMessage({ kind: 'info', text: 'Resending winner emails…' });
    try {
      const r = await fetch('/api/admin/resend-pool-winner-emails', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ date: selectedDate }),
      });
      const d = await r.json();
      if (!r.ok) {
        setMessage({ kind: 'error', text: `Resend failed: ${d.error || 'unknown'}` });
      } else {
        const breakdown = (d.results || [])
          .map((x: any) => `#${x.rank} ${x.team}: ${x.status}${x.error ? ` (${x.error})` : ''}`)
          .join(' • ');
        setMessage({
          kind: d.failed > 0 ? 'error' : 'success',
          text: `📧 Resend complete for ${selectedDate}: ${d.sent} sent / ${d.failed} failed. ${breakdown}`,
        });
      }
    } catch (e: any) {
      setMessage({ kind: 'error', text: e.message || 'Resend failed' });
    }
    setResending(false);
  };

  // v4.3.84: Force payout for individual unclaimed/blocked records.
  const handleForcePayout = async (record: ExistingRecord) => {
    const isBlocked = record.record_status === 'blocked';
    const action = isBlocked ? 'UNBLOCK & PAY' : 'FORCE PAY';
    const confirmMsg = isBlocked
      ? `⚠️ UNBLOCK & PAY record for ${record.leader_username} (Rank #${record.rank}, ${record.team_name})?\n\nThis record was marked as OVERPAYMENT. Unblocking will:\n1. Clear the overpayment flag\n2. Credit $${parseFloat(String(record.usdt_portion)).toFixed(2)} USDT\n3. Purchase $${parseFloat(String(record.share_portion)).toFixed(2)} in shares\n4. Run referral cascade\n\nThis is IRREVERSIBLE.`
      : `Force payout for ${record.leader_username} (Rank #${record.rank}, ${record.team_name})?\n\nThis will immediately credit:\n• $${parseFloat(String(record.usdt_portion)).toFixed(2)} USDT\n• $${parseFloat(String(record.share_portion)).toFixed(2)} in shares\n\nThis is IRREVERSIBLE.`;
    if (!confirm(confirmMsg)) return;

    setForcingPayout(record.id);
    setMessage({ kind: 'info', text: `⏳ Processing ${action} for #${record.rank} ${record.leader_username}…` });
    try {
      const r = await fetch('/api/admin/force-pool-payout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ record_id: record.id, unblock: isBlocked }),
      });
      const d = await r.json();
      if (!r.ok || !d.success) {
        setMessage({ kind: 'error', text: `${action} failed: ${d.error || 'unknown'}` });
      } else {
        setMessage({
          kind: 'success',
          text: `✅ ${action} complete: #${d.rank} ${d.leader_username} — $${d.usdt_portion.toFixed(2)} USDT + $${d.share_portion.toFixed(2)} shares${d.unblocked ? ' [UNBLOCKED]' : ''}. Email: ${d.email_status}`,
        });
        await loadPreview(selectedDate);
      }
    } catch (e: any) {
      setMessage({ kind: 'error', text: `${action} error: ${e.message}` });
    }
    setForcingPayout(null);
  };

  // Team deactivation — destructive deletion remains disabled.
  const handleAdminDeleteTeam = async () => {
    const id = parseInt(deleteTeamId);
    if (!id) return;
    if (!confirm(`DEACTIVATE team ID ${id}? This will hide it from the leaderboard but preserve all historical records. Team deletion has been permanently disabled.`)) return;
    try {
      const { error } = await supabase.from('teams').update({ is_active: false, updated_at: new Date().toISOString() }).eq('id', id);
      setDeleteResult(error ? `❌ ${error.message}` : '✅ Team deactivated (history preserved)');
    } catch (e: any) {
      setDeleteResult(`❌ ${e.message}`);
    }
    setDeleteTeamId('');
    loadAuxData();
  };

  const tabBtn = (id: typeof activeView, label: string) => (
    <button onClick={() => setActiveView(id)} style={{
      padding: '8px 18px', fontSize: '13px', fontWeight: 600, borderRadius: '6px',
      backgroundColor: activeView === id ? '#FFD700' : 'transparent',
      color: activeView === id ? '#000' : '#888', border: 'none', cursor: 'pointer'
    }}>{label}</button>
  );

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
        <h3 style={{ fontSize: '20px', color: '#F59E0B', fontWeight: 600 }}>⛏️ Performance Pool Payouts</h3>
        <div style={{ display: 'flex', gap: '4px', background: '#1a1a1a', borderRadius: '8px', padding: '3px' }}>
          {tabBtn('payouts', '💰 Payouts')}{tabBtn('teams', '👥 Teams & Delete')}
        </div>
      </div>

      {loading && <p style={{ color: '#888', textAlign: 'center', padding: '40px' }}>Loading...</p>}

      {!loading && activeView === 'payouts' && (
        <div>
          {/* Date selector + nav buttons + status badge (v4.3.84) */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
            <label style={{ color: '#888', fontSize: '12px' }}>Competition Date (UTC):</label>
            <button onClick={() => navigateDate(-1)} disabled={selectedDate <= POOL_LAUNCH_DATE}
              title="Previous day"
              style={{ padding: '4px 10px', borderRadius: '6px', border: '1px solid #333', background: '#1a1a1a', color: '#fff', fontSize: '14px', cursor: selectedDate <= POOL_LAUNCH_DATE ? 'not-allowed' : 'pointer', opacity: selectedDate <= POOL_LAUNCH_DATE ? 0.3 : 1 }}>◀</button>
            <input type="date" value={selectedDate} min={POOL_LAUNCH_DATE} max={todayStr}
              onChange={e => { setSelectedDate(e.target.value); setMessage(null); }}
              style={{ background: '#111', border: '1px solid #333', borderRadius: '6px', padding: '6px 12px', color: '#fff', fontSize: '13px' }} />
            <button onClick={() => navigateDate(1)} disabled={selectedDate >= todayStr}
              title="Next day"
              style={{ padding: '4px 10px', borderRadius: '6px', border: '1px solid #333', background: '#1a1a1a', color: '#fff', fontSize: '14px', cursor: selectedDate >= todayStr ? 'not-allowed' : 'pointer', opacity: selectedDate >= todayStr ? 0.3 : 1 }}>▶</button>
            <button onClick={() => loadPreview(selectedDate)} disabled={previewing}
              style={{ padding: '6px 14px', borderRadius: '6px', border: 'none', background: '#1d4ed8', color: '#fff', fontSize: '12px', fontWeight: 600, cursor: previewing ? 'not-allowed' : 'pointer', opacity: previewing ? 0.5 : 1 }}>
              {previewing ? '⏳ Loading…' : '🔍 Preview Winners'}
            </button>
            {status === 'Finalized' && <span style={{ fontSize: '11px', background: '#166534', color: '#4ade80', padding: '3px 10px', borderRadius: '12px', fontWeight: 600 }}>✓ FINALIZED ({existingCount})</span>}
            {status === 'Ready' && <span style={{ fontSize: '11px', background: '#713f12', color: '#fbbf24', padding: '3px 10px', borderRadius: '12px', fontWeight: 600 }}>READY</span>}
            {status === 'No Activity' && <span style={{ fontSize: '11px', background: '#1f1f1f', color: '#666', padding: '3px 10px', borderRadius: '12px', fontWeight: 600 }}>NO ACTIVITY</span>}
            {existingRecords.some(r => r.record_status === 'blocked') && <span style={{ fontSize: '11px', background: '#7f1d1d', color: '#f87171', padding: '3px 10px', borderRadius: '12px', fontWeight: 600 }}>⚠ BLOCKED RECORDS</span>}
            {existingRecords.some(r => r.record_status === 'unclaimed') && <span style={{ fontSize: '11px', background: '#713f12', color: '#fbbf24', padding: '3px 10px', borderRadius: '12px', fontWeight: 600 }}>⏳ UNCLAIMED</span>}
          </div>

          {/* Status message */}
          {message && (
            <div style={{ margin: '0 0 16px 0', padding: '10px 14px', borderRadius: '6px', fontSize: '12px',
              background: message.kind === 'error' ? 'rgba(127,29,29,0.25)' : message.kind === 'success' ? 'rgba(22,101,52,0.25)' : 'rgba(29,78,216,0.25)',
              color: message.kind === 'error' ? '#f87171' : message.kind === 'success' ? '#4ade80' : '#93c5fd',
              border: `1px solid ${message.kind === 'error' ? '#7f1d1d' : message.kind === 'success' ? '#166534' : '#1d4ed8'}` }}>
              {message.text}
            </div>
          )}

          {/* Pool stats */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '12px', marginBottom: '20px' }}>
            <div style={{ background: '#1a1a0a', border: '1px solid #333', borderRadius: '10px', padding: '16px', textAlign: 'center' }}>
              <div style={{ color: '#888', fontSize: '11px', textTransform: 'uppercase' }}>Pool ({selectedDate})</div>
              <div style={{ color: '#FFD700', fontSize: '28px', fontWeight: 700 }}>${poolTotal.toFixed(2)}</div>
            </div>
            <div style={{ background: '#111', border: '1px solid #222', borderRadius: '10px', padding: '16px', textAlign: 'center' }}>
              <div style={{ color: '#888', fontSize: '11px', textTransform: 'uppercase' }}>Volume Teams</div>
              <div style={{ color: '#fff', fontSize: '28px', fontWeight: 700 }}>{preview.length}</div>
            </div>
            <div style={{ background: '#111', border: '1px solid #222', borderRadius: '10px', padding: '16px', textAlign: 'center' }}>
              <div style={{ color: '#888', fontSize: '11px', textTransform: 'uppercase' }}>Remainder Bonus</div>
              <div style={{ color: '#22d3ee', fontSize: '28px', fontWeight: 700 }}>${preview.reduce((s, w) => s + (w.bonus_amount || 0), 0).toFixed(2)}</div>
            </div>
            <div style={{ background: '#111', border: '1px solid #222', borderRadius: '10px', padding: '16px', textAlign: 'center' }}>
              <div style={{ color: '#888', fontSize: '11px', textTransform: 'uppercase' }}>Total Payout</div>
              <div style={{ color: '#4ade80', fontSize: '28px', fontWeight: 700 }}>${preview.reduce((s, w) => s + w.amount_awarded, 0).toFixed(2)}</div>
            </div>
          </div>

          {/* Payout Preview — authoritative values from the backend */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
            <h4 style={{ color: '#fff', fontSize: '14px', fontWeight: 600 }}>📊 Projected Winners — Top 50</h4>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #333', color: '#888', fontSize: '10px', textTransform: 'uppercase' }}>
                  <th style={{ padding: '8px', textAlign: 'left' }}>Rank</th>
                  <th style={{ padding: '8px', textAlign: 'left' }}>Team</th>
                  <th style={{ padding: '8px', textAlign: 'left' }}>Leader</th>
                  <th style={{ padding: '8px', textAlign: 'right' }}>Volume</th>
                  <th style={{ padding: '8px', textAlign: 'center' }}>Pool %</th>
                  <th style={{ padding: '8px', textAlign: 'right' }}>Prize</th>
                  <th style={{ padding: '8px', textAlign: 'right' }}>Bonus</th>
                  <th style={{ padding: '8px', textAlign: 'right' }}>Total</th>
                  <th style={{ padding: '8px', textAlign: 'right' }}>USDT (80%)</th>
                  <th style={{ padding: '8px', textAlign: 'right' }}>Shares (20%)</th>
                </tr>
              </thead>
              <tbody>
                {preview.map((w) => {
                  const tieSize = w.tie_group_size || 1;
                  const isTied = tieSize > 1;
                  const tieStart = w.tie_rank_start || w.rank;
                  return (
                    <tr key={w.team_id} style={{ borderBottom: '1px solid #1a1a1a', color: '#ccc', background: status === 'Finalized' ? 'rgba(22,101,52,0.1)' : undefined }}>
                      <td style={{ padding: '8px', fontWeight: 700, color: tieStart <= 3 ? '#FFD700' : '#fff' }}>
                        #{w.rank}
                        {isTied && <span title={`Pro-rata split: combined prize for ranks ${tieStart}..${tieStart + tieSize - 1} / ${tieSize}`}
                          style={{ marginLeft: 6, fontSize: '9px', fontWeight: 600, padding: '1px 6px', borderRadius: '8px', background: '#713f12', color: '#fbbf24', verticalAlign: 'middle' }}>
                          TIED {tieSize}-WAY
                        </span>}
                      </td>
                      <td style={{ padding: '8px', color: '#FFD700' }}>{w.team_name}</td>
                      <td style={{ padding: '8px' }}>{w.leader_username}{w.leader_email ? <span style={{ color: '#555', fontSize: '10px' }}> ({w.leader_email})</span> : null}</td>
                      <td style={{ padding: '8px', textAlign: 'right', fontFamily: 'monospace' }}>${w.total_volume_usd.toFixed(2)}</td>
                      <td style={{ padding: '8px', textAlign: 'center' }}>{w.pct.toFixed(2)}%</td>
                      <td style={{ padding: '8px', textAlign: 'right', fontFamily: 'monospace' }}>${(w.prize_amount ?? w.amount_awarded).toFixed(2)}</td>
                      <td style={{ padding: '8px', textAlign: 'right', color: '#22d3ee', fontFamily: 'monospace' }}>${(w.bonus_amount || 0).toFixed(2)}</td>
                      <td style={{ padding: '8px', textAlign: 'right', fontWeight: 600, fontFamily: 'monospace' }}>${w.amount_awarded.toFixed(2)}</td>
                      <td style={{ padding: '8px', textAlign: 'right', color: '#4ade80', fontFamily: 'monospace' }}>${w.usdt_portion.toFixed(2)}</td>
                      <td style={{ padding: '8px', textAlign: 'right', color: '#f59e0b', fontFamily: 'monospace' }}>${w.share_portion.toFixed(2)}</td>
                    </tr>
                  );
                })}
                {preview.length === 0 && <tr><td colSpan={10} style={{ padding: '20px', textAlign: 'center', color: '#555' }}>
                  {status === 'No Activity' ? `No qualified team volume for ${selectedDate}.` : `Click "Preview Winners" to load rankings for ${selectedDate}.`}
                </td></tr>}
              </tbody>
            </table>
          </div>

          {/* Finalize button. v4.3.82: enabled even when status==='Finalized'
              if not every projected winner is recorded yet (partial run
              recovery). The per-winner endpoint is idempotent — already-
              awarded teams return 409 and are silently skipped. */}
          {(() => {
            const unawarded = Math.max(0, preview.length - existingCount);
            const canFinalize = !finalizing && !previewing && preview.length > 0 && poolTotal >= 1.0 && unawarded > 0;
            const fullyDone = status === 'Finalized' && unawarded === 0;
            return (
          <div style={{ marginTop: '20px', display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
            <button onClick={handleFinalize}
              disabled={!canFinalize}
              style={{
                padding: '10px 24px', borderRadius: '8px', border: 'none',
                cursor: canFinalize ? 'pointer' : 'not-allowed',
                background: fullyDone ? '#1f1f1f' : 'linear-gradient(135deg, #166534, #14532d)',
                color: '#fff', fontWeight: 700, fontSize: '13px',
                opacity: canFinalize ? 1 : 0.4,
              }}>
              {finalizing
                ? '⏳ Finalizing…'
                : fullyDone
                ? '✓ Already Finalized'
                : unawarded < preview.length
                ? `💸 Resume Payout (${unawarded} of ${preview.length} remaining)`
                : `💸 Finalize & Payout (${preview.length} winners)`}
            </button>
            {status === 'Finalized' && (
              <button onClick={handleResendWinnerEmails}
                disabled={resending}
                title="Re-send the congratulatory email to every finalized winner. Safe to run multiple times; logged to email_delivery_log."
                style={{
                  padding: '10px 20px', borderRadius: '8px', border: '1px solid #b8860b',
                  cursor: resending ? 'not-allowed' : 'pointer',
                  background: 'transparent', color: '#FFD700', fontWeight: 600, fontSize: '13px',
                  opacity: resending ? 0.5 : 1
                }}>
                {resending ? '⏳ Resending…' : '📧 Resend Winner Emails'}
              </button>
            )}
            <span style={{ fontSize: '10px', color: '#666' }}>
              Direct-Credit: USDT to commission balance + active shares + referral cascade. Immediate. Irreversible.
            </span>
          </div>
            );
          })()}

          {/* v4.3.84: Existing Records — Status Reconciliation View */}
          {existingRecords.length > 0 && (
            <div style={{ marginTop: '24px' }}>
              <h4 style={{ color: '#fff', fontSize: '14px', fontWeight: 600, marginBottom: '10px' }}>
                📋 Record Status — {selectedDate}
                <span style={{ fontSize: '11px', color: '#888', fontWeight: 400, marginLeft: 8 }}>
                  {existingRecords.filter(r => r.record_status === 'paid').length} paid · {existingRecords.filter(r => r.record_status === 'unclaimed').length} unclaimed · {existingRecords.filter(r => r.record_status === 'blocked').length} blocked
                </span>
              </h4>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #333', color: '#888', fontSize: '10px', textTransform: 'uppercase' }}>
                      <th style={{ padding: '8px', textAlign: 'left' }}>Rank</th>
                      <th style={{ padding: '8px', textAlign: 'left' }}>Team</th>
                      <th style={{ padding: '8px', textAlign: 'left' }}>Leader</th>
                      <th style={{ padding: '8px', textAlign: 'right' }}>Prize</th>
                      <th style={{ padding: '8px', textAlign: 'right' }}>USDT</th>
                      <th style={{ padding: '8px', textAlign: 'right' }}>Shares</th>
                      <th style={{ padding: '8px', textAlign: 'center' }}>Status</th>
                      <th style={{ padding: '8px', textAlign: 'center' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {existingRecords.map((r) => (
                      <tr key={r.id} style={{
                        borderBottom: '1px solid #1a1a1a', color: '#ccc',
                        background: r.record_status === 'blocked' ? 'rgba(127,29,29,0.1)'
                          : r.record_status === 'unclaimed' ? 'rgba(113,63,18,0.1)'
                          : 'rgba(22,101,52,0.05)',
                      }}>
                        <td style={{ padding: '8px', fontWeight: 700 }}>#{r.rank}</td>
                        <td style={{ padding: '8px', color: '#FFD700' }}>{r.team_name}</td>
                        <td style={{ padding: '8px' }}>{r.leader_username}</td>
                        <td style={{ padding: '8px', textAlign: 'right', fontFamily: 'monospace' }}>${parseFloat(String(r.amount_awarded)).toFixed(2)}</td>
                        <td style={{ padding: '8px', textAlign: 'right', color: '#4ade80', fontFamily: 'monospace' }}>${parseFloat(String(r.usdt_portion)).toFixed(2)}</td>
                        <td style={{ padding: '8px', textAlign: 'right', color: '#f59e0b', fontFamily: 'monospace' }}>${parseFloat(String(r.share_portion)).toFixed(2)}</td>
                        <td style={{ padding: '8px', textAlign: 'center' }}>
                          {r.record_status === 'paid' && (
                            <span style={{ fontSize: '10px', background: '#166534', color: '#4ade80', padding: '2px 8px', borderRadius: '10px', fontWeight: 600 }}>
                              ✓ PAID {r.paid_at ? new Date(r.paid_at).toLocaleDateString() : ''}
                            </span>
                          )}
                          {r.record_status === 'unclaimed' && (
                            <span style={{ fontSize: '10px', background: '#713f12', color: '#fbbf24', padding: '2px 8px', borderRadius: '10px', fontWeight: 600 }}>
                              ⏳ UNCLAIMED
                            </span>
                          )}
                          {r.record_status === 'blocked' && (
                            <span style={{ fontSize: '10px', background: '#7f1d1d', color: '#f87171', padding: '2px 8px', borderRadius: '10px', fontWeight: 600 }}>
                              🚫 BLOCKED (Overpayment)
                            </span>
                          )}
                        </td>
                        <td style={{ padding: '8px', textAlign: 'center' }}>
                          {r.record_status === 'unclaimed' && (
                            <button onClick={() => handleForcePayout(r)}
                              disabled={forcingPayout === r.id}
                              style={{
                                padding: '4px 12px', borderRadius: '6px', border: 'none', fontSize: '11px', fontWeight: 600,
                                background: 'linear-gradient(135deg, #1d4ed8, #1e40af)', color: '#fff',
                                cursor: forcingPayout === r.id ? 'not-allowed' : 'pointer',
                                opacity: forcingPayout === r.id ? 0.5 : 1,
                              }}>
                              {forcingPayout === r.id ? '⏳…' : '⚡ Force Pay'}
                            </button>
                          )}
                          {r.record_status === 'blocked' && (
                            <button onClick={() => handleForcePayout(r)}
                              disabled={!!forcingPayout}
                              style={{
                                padding: '4px 12px', borderRadius: '6px', border: '1px solid #7f1d1d', fontSize: '11px', fontWeight: 600,
                                background: 'transparent', color: '#f87171',
                                cursor: forcingPayout ? 'not-allowed' : 'pointer',
                                opacity: forcingPayout ? 0.5 : 1,
                              }}>
                              {forcingPayout === r.id ? '⏳…' : '🔓 Unblock & Pay'}
                            </button>
                          )}
                          {r.record_status === 'paid' && (
                            <span style={{ fontSize: '10px', color: '#555' }}>—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Pool history */}
          <h4 style={{ color: '#fff', fontSize: '14px', fontWeight: 600, marginTop: '30px', marginBottom: '10px' }}>📅 Pool History (Last 14 Days)</h4>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <thead><tr style={{ borderBottom: '1px solid #333', color: '#888', fontSize: '10px', textTransform: 'uppercase' }}>
              <th style={{ padding: '8px', textAlign: 'left' }}>Date</th>
              <th style={{ padding: '8px', textAlign: 'right' }}>Shares Sold</th>
              <th style={{ padding: '8px', textAlign: 'right' }}>Revenue</th>
              <th style={{ padding: '8px', textAlign: 'right' }}>{POOL_CONTRIBUTION_PERCENT}% Pool</th>
              <th style={{ padding: '8px', textAlign: 'right' }}>Purchases</th>
            </tr></thead>
            <tbody>{poolHistory.map((d: any) => {
              const rev = parseFloat(d.revenue) || 0;
              const shares = parseFloat(d.shares_sold) || 0;
              const pool = parseFloat(d.total) || 0;
              return (
                <tr key={d.day} style={{ borderBottom: '1px solid #1a1a1a', color: '#ccc' }}>
                  <td style={{ padding: '8px' }}>{d.day}</td>
                  <td style={{ padding: '8px', textAlign: 'right', fontFamily: 'monospace' }}>{shares.toFixed(2)}</td>
                  <td style={{ padding: '8px', textAlign: 'right', fontFamily: 'monospace' }}>${rev.toFixed(2)}</td>
                  <td style={{ padding: '8px', textAlign: 'right', color: '#FFD700', fontWeight: 600, fontFamily: 'monospace' }}>${pool.toFixed(2)}</td>
                  <td style={{ padding: '8px', textAlign: 'right' }}>{d.entry_count}</td>
                </tr>
              );
            })}</tbody>
          </table>
        </div>
      )}

      {!loading && activeView === 'teams' && (
        <div>
          {/* Delete team utility */}
          <div style={{ background: '#1a0a0a', border: '1px solid #7f1d1d40', borderRadius: '10px', padding: '16px', marginBottom: '20px' }}>
            <h4 style={{ color: '#f87171', fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}>🗑 Delete Team (Testing)</h4>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input type="number" value={deleteTeamId} onChange={e => setDeleteTeamId(e.target.value)} placeholder="Team ID"
                style={{ background: '#111', border: '1px solid #333', borderRadius: '6px', padding: '8px 12px', color: '#fff', fontSize: '13px', width: '120px' }} />
              <button onClick={handleAdminDeleteTeam} disabled={!deleteTeamId}
                style={{ padding: '8px 16px', borderRadius: '6px', border: 'none', background: '#7f1d1d', color: '#fff', fontSize: '12px', fontWeight: 600, cursor: deleteTeamId ? 'pointer' : 'not-allowed', opacity: deleteTeamId ? 1 : 0.3 }}>
                Delete
              </button>
              {deleteResult && <span style={{ fontSize: '12px', color: deleteResult.startsWith('✅') ? '#4ade80' : '#f87171' }}>{deleteResult}</span>}
            </div>
          </div>

          {/* Team list */}
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <thead><tr style={{ borderBottom: '1px solid #333', color: '#888', fontSize: '10px', textTransform: 'uppercase' }}>
              <th style={{ padding: '8px', textAlign: 'left' }}>ID</th>
              <th style={{ padding: '8px', textAlign: 'left' }}>Team Name</th>
              <th style={{ padding: '8px', textAlign: 'left' }}>Leader</th>
              <th style={{ padding: '8px', textAlign: 'center' }}>Category</th>
            </tr></thead>
            <tbody>{teams.map((t: any) => (
              <tr key={t.id} style={{ borderBottom: '1px solid #1a1a1a', color: '#ccc' }}>
                <td style={{ padding: '8px', fontFamily: 'monospace' }}>{t.id}</td>
                <td style={{ padding: '8px', color: '#FFD700', fontWeight: 600 }}>{t.name}</td>
                <td style={{ padding: '8px' }}>{t.leader_username}</td>
                <td style={{ padding: '8px', textAlign: 'center' }}><span style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '8px', background: '#222', textTransform: 'uppercase' }}>{t.category}</span></td>
              </tr>
            ))}</tbody>
          </table>
          {teams.length === 0 && <p style={{ color: '#555', textAlign: 'center', padding: '20px' }}>No teams registered.</p>}
        </div>
      )}
    </div>
  );
};

export default AdminPerformancePoolPayouts;
